import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckIcon, MapPinIcon, QrCodeIcon, XIcon } from 'lucide-react';
import type { FanSource } from '../../data/fans';
import { Button } from '../ui/Button';
interface CreateCaptureSourceModalProps {
  open: boolean;
  onCreate: (source: Omit<FanSource, 'id' | 'scans' | 'joins'>) => void;
  onClose: () => void;
}
export function CreateCaptureSourceModal({
  open,
  onCreate,
  onClose
}: CreateCaptureSourceModalProps) {
  const [label, setLabel] = useState('');
  const [detail, setDetail] = useState('');
  const submit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!label.trim() || !detail.trim()) return;
    onCreate({
      label: label.trim(),
      detail: detail.trim()
    });
    setLabel('');
    setDetail('');
    onClose();
  };
  return <AnimatePresence data-id="element-336" data-mp="br3ebbqj1ita">{open && <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-337" data-mp="grhmquw1iuru"><motion.button aria-label="Close capture campaign creator" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="absolute inset-0 bg-black/70 backdrop-blur-sm" data-id="element-338" data-mp="gssluhejjecm" /><motion.section initial={{
        opacity: 0,
        scale: 0.97,
        y: 12
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.97,
        y: 12
      }} transition={{
        type: 'spring',
        stiffness: 310,
        damping: 28
      }} role="dialog" aria-modal="true" aria-labelledby="capture-source-title" className="relative z-10 w-full max-w-md overflow-hidden rounded-3xl border border-white/[0.09] bg-ink-900 shadow-2xl" data-id="element-339" data-mp="12ja77a37s4ot"><header className="flex items-start justify-between border-b border-white/[0.06] p-5" data-id="element-340" data-mp="1ldywypiea8je"><div data-id="element-341" data-mp="1z6y7ed3blck3"><div className="mb-2 flex items-center gap-1.5 text-[12px] font-medium uppercase tracking-[0.14em] text-[#e8a369]" data-id="element-342" data-mp="1z6oiksu0hbfz"><QrCodeIcon className="h-3.5 w-3.5" data-id="element-343" data-mp="1v7z5z2gsrxxe" /> Capture campaign</div><h2 id="capture-source-title" className="text-[20px] font-semibold tracking-tight text-white" data-id="element-344" data-mp="296p2ogzpfvl8">Create a campaign source</h2><p className="mt-1 text-[12.5px] leading-relaxed text-zinc-500" data-id="element-345" data-mp="248dx4igg078t">Create one source for each placement you want to measure.</p></div><button type="button" onClick={onClose} aria-label="Close" className="rounded-lg p-1.5 text-zinc-400 transition-colors hover:bg-white/[0.06] hover:text-white" data-id="element-346" data-mp="15uq5tjf91oti"><XIcon className="h-5 w-5" data-id="element-347" data-mp="1as9vrglb7q1a" /></button></header><form onSubmit={submit} data-id="element-348" data-mp="1fkjjcpyyce0e"><div className="space-y-4 p-5" data-id="element-349" data-mp="1fkt7s94cmd3z"><Field label="Campaign name" value={label} onChange={setLabel} placeholder="e.g. Billy Bob’s · Oct 16" helper="Use the venue, placement, and date so it is easy to find later." data-id="element-350" data-mp="1fm4a04i5bcsz" /><Field label="Placement detail" value={detail} onChange={setDetail} placeholder="e.g. Merch table sign" icon={MapPinIcon} helper="Describe where fans will see the QR code or link." data-id="element-351" data-mp="1pho6as0mgdi9" /><div className="rounded-xl border border-[#d97742]/20 bg-[#d97742]/[0.06] p-3 text-[12px] leading-relaxed text-zinc-300" data-id="element-352" data-mp="1yjxdb7woloqw">ArtistOS will generate a dedicated Backstage QR code and shareable link as soon as you create this source.</div></div><footer className="flex justify-end gap-2 border-t border-white/[0.06] p-4" data-id="element-353" data-mp="45wg0rgs59sx"><Button type="button" variant="ghost" size="sm" onClick={onClose} data-id="element-354" data-mp="3ssb0dvqu6d5p">Cancel</Button><Button type="submit" variant="primary" size="sm" className="bg-[#d97742] hover:bg-[#e08d59] shadow-none" data-id="element-355" data-mp="3o012ntjhc8sv"><CheckIcon className="h-3.5 w-3.5" data-id="element-356" data-mp="3j0wun68akh44" /> Create QR campaign</Button></footer></form></motion.section></div>}</AnimatePresence>;
}
function Field({
  label,
  value,
  onChange,
  placeholder,
  helper,
  icon: Icon
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  helper: string;
  icon?: React.ComponentType<{
    className?: string;
  }>;
}) {
  return <label className="block" data-id="element-357" data-mp="ajtkqfi1ylmr"><span className="mb-1.5 flex items-center gap-1.5 text-[12px] font-medium text-zinc-400" data-id="element-358" data-mp="5m0i1wfyk9r8">{Icon && <Icon className="h-3.5 w-3.5 text-zinc-600" data-id="element-359" data-mp="1lhonxyf2vmx" />}{label}</span><input required value={value} onChange={event => onChange(event.target.value)} placeholder={placeholder} className="w-full rounded-xl border border-white/[0.1] bg-ink-800 px-3 py-2.5 text-[13px] text-white outline-none placeholder:text-zinc-600 focus:border-[#d97742]/70 focus:ring-2 focus:ring-[#d97742]/15" data-id="element-360" data-mp="1n163rvt7cqa" /><span className="mt-1.5 block text-[11px] leading-relaxed text-zinc-600" data-id="element-361" data-mp="e0o35tg0uy34">{helper}</span></label>;
}